// ** React Import
//import { useState } from 'react';

// ** Custom Components
import Sidebar from '@components/sidebar';
import InputPasswordToggle from '@components/input-password-toggle';
import { handleError } from '@components/toast';

// ** Utils
import { selectThemeColors } from '@utils';
import codes from '../../utility/codes';

// ** Third Party Components
import * as yup from 'yup';
import { Formik, Field, Form } from 'formik';
import Select from 'react-select';

// ** Reactstrap Imports
import { Button, Label, FormFeedback, Input, Row, Col } from 'reactstrap';

// ** Graphql
import { CREATE, UPDATE } from '../../graphql/Mutation/admins';
import { useMutation } from '@apollo/client';
import { permissionsOptions } from '../../utility/Options';

const FormSidebar = ({
  open,
  toggleSidebar,
  defaultValues,
  handleFetching
}) => {
  // ** mutations **
  const [fechAdmin, { loading }] = useMutation(
    defaultValues?._id ? UPDATE : CREATE
  );
  // ** States
  const handleSidebarClosed = () => { };

  const statusOptions = [
    { value: 'ACTIVE', label: 'Activo' },
    { value: 'INACTIVE', label: 'Inactivo' }
  ];

  const codesOptions = codes.map((country) => {
    return {
      value: country.dial_code,
      label: `${country.dial_code} ${country.name_es}`
    };
  });

  // ** Schema yup
  const Schema = yup.object().shape({
    fullName: yup.string().required('Ingresa tu nombre completo'),
    email: yup
      .string()
      .email('Correo inválido')
      .required('Tienes que ingresar un correo'),
    password: defaultValues?._id
      ? yup.string().min(8, 'Minimo 8 caracteres').max(16)
      : yup
        .string()
        .min(8, 'Minimo 8 caracteres')
        .max(16)
        .required('Contraseña es requerida'),
    phone: yup
      .string()
      .min(6, 'Ingresa un número de celular')
      .max(15)
      .required('Ingresa un número de celular'),
    permissions: yup
      .array()
      .min(1, 'Selecciona al menos 1 permiso')
      .of(yup.string().required('Selecciona al menos 1 permiso'))
      .required('Selecciona al menos 1 permiso')
  });

  const onSubmit = async (values) => {
    const data = {
      fullName: values.fullName,
      email: values.email,
      phone: `${values.phonecode}${values.phone}`,
      phonecode: values.phonecode,
      password: values.password,
      permissions: values.permissions,
      status: values.status
    };
    let variables = { input: data };
    if (defaultValues?._id) {
      variables = {
        input: {
          _id: defaultValues._id,
          data: {
            fullName: values.fullName,
            email: values.email,
            phone: `${values.phonecode}${values.phone}`,
            phonecode: values.phonecode,
            permissions: values.permissions,
            status: values.status
          }
        }
      };
    }
    try {
      await fechAdmin({
        variables
      });
      handleFetching();
      toggleSidebar();
    } catch (e) {
      handleError(e);
    }
  };

  const handleValidateForm = async ({ validateForm, setErrors, values }) => {
    const errors = await validateForm();
    if (Object.keys(errors).length) {
      setErrors(errors);
      return;
    }
    await onSubmit(values);
  };

  const onlyNumbers = ({ e, setFieldValue, field }) => {
    const result = e.target.value.replace(/\D/g, '');
    setFieldValue(field, result);
  };

  const Checkbox = (props) => {
    return (
      <Field name={props.name}>
        {({ field, form }) => (
          <div className="form-check form-check-inline">
            <Input
              {...field}
              type="checkbox"
              id={props.value}
              checked={field.value.includes(props.value)}
              onChange={() => {
                const set = new Set(field.value);
                if (set.has(props.value)) {
                  set.delete(props.value);
                } else {
                  set.add(props.value);
                }
                form.setFieldValue(field.name, Array.from(set));
              }}
            />
            <Label for={props.value} className="form-check-label">
              {props.label}
            </Label>
          </div>
        )}
      </Field>
    );
  };

  return (
    <Sidebar
      size="lg"
      open={open}
      title={
        defaultValues?._id ? 'Editar Administrador' : 'Nuevo Administrador'
      }
      headerClassName="mb-1"
      contentClassName="pt-0"
      toggleSidebar={toggleSidebar}
      onClosed={handleSidebarClosed}
    >
      <Formik
        initialValues={defaultValues}
        validateOnChange
        validateOnBlur
        validationSchema={Schema}
      >
        {({
          values,
          handleChange,
          handleBlur,
          errors,
          setErrors,
          validateForm,
          setFieldValue
        }) => (
          <Form autoComplete="off">
            <div className="mb-1">
              <Label className="form-label" for="fullName">
                Nombre Completo <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="fullName"
                id="fullName"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.fullName}
                placeholder="Nombre completo"
                invalid={errors.fullName && true}
              />
              {errors.fullName && (
                <FormFeedback>{errors.fullName}</FormFeedback>
              )}
            </div>
            <div className="mb-1">
              <Label className="form-label" for="phone">
                Teléfono <span className="text-danger">*</span>
              </Label>
              <Row>
                <Col md="4">
                  <Select
                    name="phonecode"
                    id="phonecode"
                    isClearable={false}
                    classNamePrefix="select"
                    theme={selectThemeColors}
                    defaultValue={codesOptions.find(
                      (option) => option.value === values.phonecode
                    )}
                    onChange={(selectedOption) => {
                      setFieldValue('phonecode', selectedOption.value);
                    }}
                    options={codesOptions}
                  />
                </Col>
                <Col md="8">
                  <Input
                    type="text"
                    name="phone"
                    id="phone"
                    onChange={(e) =>
                      onlyNumbers({ e, setFieldValue, field: 'phone' })
                    }
                    maxLength={15}
                    minLength={6}
                    onBlur={handleBlur}
                    value={values.phone}
                    placeholder="3001234567"
                    invalid={errors.phone && true}
                  />
                  {errors.phone && <FormFeedback>{errors.phone}</FormFeedback>}
                </Col>
              </Row>
            </div>
            <div className="mb-1">
              <Label className="form-label" for="email">
                Correo <span className="text-danger">*</span>
              </Label>
              <Input
                type="email"
                name="email"
                id="email_admin"
                autoComplete="new-email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                placeholder="admin@dominio.com"
                invalid={errors.email && true}
              />
              {errors.email && <FormFeedback>{errors.email}</FormFeedback>}
            </div>
            <div className="mb-1">
              <Label className="form-label" for="password">
                Contraseña {!defaultValues?._id && <span className="text-danger">*</span>}
              </Label>
              <InputPasswordToggle
                className="input-group-merge"
                invalid={errors.password && true}
                onChange={handleChange}
                autoComplete="new-password"
                name="password"
                disabled={!!defaultValues._id}
                onBlur={handleBlur}
                id="password_admin"
                value={values.password}
              />
              {errors.password && (
                <FormFeedback>{errors.password}</FormFeedback>
              )}
            </div>
            <div className="mb-1">
              <Label className="form-label">
                Secciones <span className="text-danger">*</span>
              </Label>
              <Row>
                {permissionsOptions.map((item, index) => (
                  <Col md="6" className="mt-1" key={index}>
                    <Checkbox
                      name="permissions"
                      value={item.value}
                      label={item.label}
                    />
                  </Col>
                ))}
              </Row>
              {errors.permissions && (
                <div className="text-danger">{errors.permissions}</div>
              )}
            </div>
            <div className="mb-1">
              <Label className="form-label" for="membership">
                Estado <span className="text-danger">*</span>
              </Label>
              <Select
                name="status"
                id="status"
                isClearable={false}
                classNamePrefix="select"
                placeholder="Selecciona un estado"
                theme={selectThemeColors}
                defaultValue={statusOptions.find(
                  (option) => option.value === values.status
                )}
                onChange={(selectedOption) => {
                  setFieldValue('status', selectedOption.value);
                }}
                options={statusOptions}
              />
            </div>
            <Button
              className="me-1 mt-1"
              color="primary"
              onClick={() =>
                handleValidateForm({ validateForm, setErrors, values })
              }
            >
              {loading
                ? `Procesando...`
                : defaultValues?._id
                  ? `Actualizar`
                  : `Crear`}
            </Button>
          </Form>
        )}
      </Formik>
    </Sidebar>
  );
};

export default FormSidebar;
